/**
 * Watch schedules (« veilles »): adaptive, every N minutes, daily at a local time, or a 5-field cron expression in the
 * user's timezone. Shared by the Worker (next run instants) and the web app (human preview). DST-safe: every local
 * instant goes through zonedToUtc.
 */
import { isValidHm, parseHm, wall, zonedToUtc } from "./time";

export type WatchSchedule =
  | { kind: "adaptive" }
  | { kind: "every"; minutes: number }
  | { kind: "daily"; time: string }
  | { kind: "cron"; expr: string };

/** Bounds of an "every N minutes" schedule: 15 minutes to one week. */
export const EVERY_MIN_MINUTES = 15;
export const EVERY_MAX_MINUTES = 7 * 24 * 60;

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;

interface CronFields {
  minutes: number[];
  hours: number[];
  days: Set<number>;
  months: Set<number>;
  weekdays: Set<number>;
  /** The day-of-month / day-of-week fields were restricted ("*" otherwise): cron ORs them when both are. */
  daysRestricted: boolean;
  weekdaysRestricted: boolean;
}

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const DAY_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function parseField(field: string, min: number, max: number, names: string[] = [], nameBase = 0): number[] | null {
  const out = new Set<number>();
  const value = (s: string): number | null => {
    const lower = s.toLowerCase();
    const named = names.indexOf(lower);
    if (named >= 0) return named + nameBase;
    if (!/^\d{1,2}$/.test(s)) return null;
    return Number(s);
  };
  for (const part of field.split(",")) {
    const m = /^(\*|[a-z0-9]+(?:-[a-z0-9]+)?)(?:\/(\d{1,2}))?$/i.exec(part.trim());
    if (!m) return null;
    const step = m[2] ? Number(m[2]) : 1;
    if (!(step >= 1)) return null;
    let lo = min;
    let hi = max;
    if (m[1] !== "*") {
      const [a, b] = m[1]!.split("-");
      const va = value(a!);
      const vb = b === undefined ? (m[2] ? max : va) : value(b);
      if (va === null || vb === null) return null;
      lo = va;
      hi = vb;
    }
    if (lo < min || hi > max || lo > hi) return null;
    for (let v = lo; v <= hi; v += step) out.add(v);
  }
  return out.size ? [...out].sort((a, b) => a - b) : null;
}

/** Parses a 5-field cron expression (minute hour day-of-month month day-of-week); null when invalid. */
export function parseCron(expr: string): CronFields | null {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [mi, ho, dom, mon, dow] = parts as [string, string, string, string, string];
  const minutes = parseField(mi, 0, 59);
  const hours = parseField(ho, 0, 23);
  const days = parseField(dom, 1, 31);
  const months = parseField(mon, 1, 12, MONTH_NAMES, 1);
  const weekdays = parseField(dow, 0, 7, DAY_NAMES, 0);
  if (!minutes || !hours || !days || !months || !weekdays) return null;
  return {
    minutes,
    hours,
    days: new Set(days),
    months: new Set(months),
    weekdays: new Set(weekdays.map((d) => d % 7)),
    daysRestricted: dom !== "*",
    weekdaysRestricted: dow !== "*",
  };
}

export function isValidCron(expr: string): boolean {
  return parseCron(expr) !== null;
}

function dayMatches(f: CronFields, y: number, mo: number, d: number): boolean {
  if (!f.months.has(mo)) return false;
  const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  if (f.daysRestricted && f.weekdaysRestricted) return f.days.has(d) || f.weekdays.has(dow);
  return f.days.has(d) && f.weekdays.has(dow);
}

/** First instant strictly after `after` matching the cron expression in `tz` (horizon: 400 days); null when none. */
export function nextCron(expr: string, tz: string, after: number): number | null {
  const f = parseCron(expr);
  if (!f) return null;
  const start = wall(tz, after);
  for (let i = 0; i < 400; i++) {
    const t = new Date(Date.UTC(start.y, start.mo - 1, start.d) + i * DAY);
    const y = t.getUTCFullYear();
    const mo = t.getUTCMonth() + 1;
    const d = t.getUTCDate();
    if (!dayMatches(f, y, mo, d)) continue;
    for (const h of f.hours) {
      for (const mi of f.minutes) {
        const at = zonedToUtc(tz, y, mo, d, h, mi);
        if (at > after) return at;
      }
    }
  }
  return null;
}

/** First local `hm` strictly after `after`. */
export function nextDailyAt(hm: string, tz: string, after: number): number {
  const { h, m } = parseHm(hm);
  const w = wall(tz, after);
  for (let i = 0; i < 3; i++) {
    const t = new Date(Date.UTC(w.y, w.mo - 1, w.d) + i * DAY);
    const at = zonedToUtc(tz, t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate(), h, m);
    if (at > after) return at;
  }
  return after + DAY;
}

/** A schedule is valid (bounds, time format, cron syntax). Returns a French error or null. */
export function scheduleProblem(s: WatchSchedule): string | null {
  switch (s.kind) {
    case "adaptive":
      return null;
    case "every":
      return Number.isInteger(s.minutes) && s.minutes >= EVERY_MIN_MINUTES && s.minutes <= EVERY_MAX_MINUTES
        ? null
        : `Fréquence entre ${EVERY_MIN_MINUTES} minutes et 7 jours.`;
    case "daily":
      return isValidHm(s.time) ? null : "Heure invalide (HH:MM).";
    case "cron": {
      const f = parseCron(s.expr);
      if (!f) return "Expression cron invalide (5 champs : minute heure jour mois jour-de-la-semaine).";
      // At most every 15 minutes: two consecutive minutes of the same hour closer than that are refused.
      for (let i = 1; i < f.minutes.length; i++) if (f.minutes[i]! - f.minutes[i - 1]! < EVERY_MIN_MINUTES) return `Une veille tourne au plus toutes les ${EVERY_MIN_MINUTES} minutes.`;
      if (f.minutes.length > 1 && 60 - f.minutes[f.minutes.length - 1]! + f.minutes[0]! < EVERY_MIN_MINUTES && f.hours.length > 1) return `Une veille tourne au plus toutes les ${EVERY_MIN_MINUTES} minutes.`;
      return null;
    }
  }
}

/**
 * Next run of a fixed schedule after `after` (adaptive schedules: `after + adaptiveMinutes`). `anchor` (the previous
 * planned run) keeps "every N" on its grid instead of drifting with the run duration.
 */
export function nextRun(s: WatchSchedule, tz: string, after: number, opts: { anchor?: number | null; adaptiveMinutes?: number } = {}): number | null {
  switch (s.kind) {
    case "adaptive":
      return after + Math.max(EVERY_MIN_MINUTES, opts.adaptiveMinutes ?? 240) * MINUTE;
    case "every": {
      const step = s.minutes * MINUTE;
      const anchor = opts.anchor ?? after;
      if (anchor > after) return anchor;
      const n = Math.floor((after - anchor) / step) + 1;
      return anchor + n * step;
    }
    case "daily":
      return nextDailyAt(s.time, tz, after);
    case "cron":
      return nextCron(s.expr, tz, after);
  }
}

const FR_DAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function hm(h: number, m: number): string {
  return `${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}`;
}

function frList(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}

function everyMinutesText(minutes: number): string {
  if (minutes % (24 * 60) === 0) return minutes === 24 * 60 ? "tous les jours" : `tous les ${minutes / (24 * 60)} jours`;
  if (minutes % 60 === 0) return minutes === 60 ? "toutes les heures" : `toutes les ${minutes / 60} heures`;
  return `toutes les ${minutes} minutes`;
}

/** French description of a cron expression ("du lundi au vendredi à 8 h 30", "toutes les 3 heures"…). */
export function describeCron(expr: string): string {
  const f = parseCron(expr);
  if (!f) return "expression cron invalide";
  const parts = expr.trim().split(/\s+/) as [string, string, string, string, string];
  let when = "";
  const minuteStep = /^\*\/(\d+)$/.exec(parts[0]);
  const hourStep = /^\*\/(\d+)$/.exec(parts[1]);
  if (minuteStep && parts[1] === "*") when = everyMinutesText(Number(minuteStep[1]));
  else if (hourStep && f.minutes.length === 1) when = `${everyMinutesText(Number(hourStep[1]) * 60)}${f.minutes[0] ? ` (à la minute ${f.minutes[0]})` : " (à l'heure pile)"}`;
  else if (parts[1] === "*" && f.minutes.length === 1) when = `toutes les heures${f.minutes[0] ? ` à la minute ${f.minutes[0]}` : " (à l'heure pile)"}`;
  else if (f.hours.length * f.minutes.length <= 6) when = `à ${frList(f.hours.flatMap((h) => f.minutes.map((m) => hm(h, m))))}`;
  else when = `à ${f.hours.length * f.minutes.length} moments de la journée`;
  let days = "";
  if (f.weekdaysRestricted) {
    const wd = [...f.weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
    const key = wd.join(",");
    if (key === "1,2,3,4,5") days = "du lundi au vendredi";
    else if (key === "6,0") days = "le week-end";
    else days = `le ${frList(wd.map((d) => FR_DAYS[d]!))}`;
  }
  if (f.daysRestricted) days = `${days ? `${days} ou ` : ""}le ${frList([...f.days].sort((a, b) => a - b).map((d) => (d === 1 ? "1er" : String(d))))} du mois`;
  if (parts[3] !== "*") days = `${days ? `${days}, ` : ""}en ${frList([...f.months].sort((a, b) => a - b).map((m) => FR_MONTHS[m - 1]!))}`;
  if (!days && !minuteStep && !hourStep && parts[1] !== "*") days = "tous les jours";
  return [days, when].filter(Boolean).join(" ");
}

/** French description of a watch schedule. */
export function describeSchedule(s: WatchSchedule, adaptiveMinutes?: number): string {
  switch (s.kind) {
    case "adaptive":
      return adaptiveMinutes ? `adaptatif (en ce moment ${everyMinutesText(adaptiveMinutes).replace(/^tous les jours$/, "une fois par jour")})` : "adaptatif";
    case "every":
      return everyMinutesText(s.minutes);
    case "daily": {
      const { h, m } = parseHm(s.time);
      return `tous les jours à ${hm(h, m)}`;
    }
    case "cron":
      return describeCron(s.expr);
  }
}

/** The next `count` run instants of a fixed schedule (preview). */
export function nextRuns(s: WatchSchedule, tz: string, from: number, count = 3): number[] {
  if (s.kind === "adaptive") return [];
  const out: number[] = [];
  let after = from;
  let anchor: number | null = s.kind === "every" ? from + s.minutes * MINUTE : null;
  for (let i = 0; i < count; i++) {
    const next = s.kind === "every" ? anchor! : nextRun(s, tz, after);
    if (next === null) break;
    out.push(next);
    after = next;
    if (s.kind === "every") anchor = next + s.minutes * MINUTE;
  }
  return out;
}
